import React from "react";
import "./css/RequestedActionError.css";

/**
 * Generic error component for handling common HTTP error responses
 * Displays appropriate messages and UI for different error status codes
 */
const RequestedActionError = ({
  statusCode,
  message,
  actionName = "perform this action",
  onRetry = null,
  onClose = null,
}) => {
  /**
   * Get standardized error information based on status code
   */
  const getErrorInfo = (code) => {
    switch (code) {
      case 401:
        return {
          title: "Authentication Required",
          description: "You need to log in to access this feature.",
          icon: "🔒",
          suggestion: "Please log in and try again.",
        };
      case 403:
        return {
          title: "Access Denied",
          description: "You don't have permission to perform this action.",
          icon: "🚫",
          suggestion:
            "Contact your administrator if you believe you should have access to this feature.",
        };
      case 404:
        return {
          title: "Not Found",
          description: "The requested resource could not be found.",
          icon: "❓",
          suggestion:
            "Please check the URL or contact support if the problem persists.",
        };
      case 429:
        return {
          title: "Too Many Requests",
          description:
            "You've made too many requests. Please wait before trying again.",
          icon: "⏱️",
          suggestion: "Wait a few minutes before attempting this action again.",
        };
      case 500:
        return {
          title: "Server Error",
          description: "An internal server error occurred.",
          icon: "⚠️",
          suggestion:
            "Please try again later or contact support if the problem persists.",
        };
      case 502:
      case 503:
      case 504:
        return {
          title: "Service Unavailable",
          description: "The service is temporarily unavailable.",
          icon: "🔧",
          suggestion: "Please try again in a few minutes.",
        };
      default:
        return {
          title: "Error",
          description: "An unexpected error occurred.",
          icon: "❌",
          suggestion:
            "Please try again or contact support if the problem persists.",
        };
    }
  };

  const errorInfo = getErrorInfo(statusCode);
  const isServerError = [500, 502, 503, 504].includes(statusCode);

  return (
    <div className="requested-action-error" data-status={statusCode}>
      <div className="error-content">
        <div className="error-icon">{errorInfo.icon}</div>

        <div className="error-details">
          <h3 className="error-title">{errorInfo.title}</h3>

          <div className="error-messages">
            <p className="error-description">
              {isServerError
                ? errorInfo.description
                : message || errorInfo.description}
            </p>

            {!isServerError && actionName !== "perform this action" && (
              <p className="error-action-context">Unable to {actionName}.</p>
            )}

            <p className="error-suggestion">{errorInfo.suggestion}</p>
          </div>

          {statusCode && (
            <div className="error-code">
              <small>Error Code: {statusCode}</small>
            </div>
          )}
        </div>
      </div>

      <div className="error-actions">
        {onRetry && (
          <button onClick={onRetry} className="rae-retry-button" type="button">
            Try Again
          </button>
        )}

        {isServerError && (
          <a
            href={`mailto:support@kinprotect.com?subject=Error Report - Server Error (${statusCode})&body=I encountered a server error while using the app.%0A%0AError Code: ${statusCode}%0A%0APlease describe what you were doing:%0A`}
            className="rae-report-button"
          >
            Report Issue
          </a>
        )}

        {onClose && (
          <button onClick={onClose} className="rae-close-button" type="button">
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default RequestedActionError;
